import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, Text } from 'src/packages/core';
import { DarkTheme, LightTheme, ThemeType, useTheme } from 'src/packages/theme';
import { ChevronRightIcon, ChevronLeftIcon, AppleLogo, GooglePlayLogo } from 'src/packages/icons';
import AppDownloadQrCode from '../../../common/app-download-page-qr-code.png';

import { goToUrl } from 'src/utils/go-to-url';
import { config } from 'src/config';

interface DownloadIconPopoverContentProps {
  theme: ThemeType;
}

export const DownloadIconPopoverContent = ({ theme }: DownloadIconPopoverContentProps) => {
  const { t } = useTranslation();
  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  const ChevronIcon = contextTheme.rtl ? ChevronLeftIcon : ChevronRightIcon;

  return useMemo(
    () => (
      <Flex
        width={'100%'}
        flexDirection={'column'}
        p={4}
        gap={'16px'}
        alignItems={'center'}
        backgroundColor={defaultTheme.colors.background.secondary.BG3}
      >
        <Flex flexDirection={'column'} alignItems={'center'} justifyContent={'center'}>
          <Text
            variant={'p3'}
            text={t('download_app')}
            mb={2}
            color={
              theme === 'light' ? LightTheme.colors.text.primary : DarkTheme.colors.text.secondary
            }
          />

          <img src={AppDownloadQrCode} alt='app-download-page-qr-code' width={100} height={100} />
        </Flex>

        <Flex flexDirection={'column'} gap={'8px'} alignItems={'center'} justifyContent={'center'}>
          <Button
            text={t('button.app_store')}
            variant='tertiarySmall'
            leftIcon={<AppleLogo />}
            width={'100%'}
            onClick={() => goToUrl(config.iosTestflightUrl, '_blank')}
          />
          <Button
            text={t('button.google_play')}
            variant='tertiarySmall'
            width={'100%'}
            leftIcon={<GooglePlayLogo />}
            onClick={() => goToUrl(config.androidAppUrl, '_blank')}
          />

          <Flex
            alignItems={'center'}
            gap={1}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              goToUrl(config.coinflareAppDownloadUrl);
            }}
          >
            <Text
              variant={'p2'}
              text={t('more_options')}
              color={
                theme === 'light'
                  ? LightTheme.colors.text.primary
                  : DarkTheme.colors.palette.primary
              }
            />

            <ChevronIcon
              color={
                theme === 'light'
                  ? LightTheme.colors.text.primary
                  : DarkTheme.colors.palette.primary
              }
            />
          </Flex>
        </Flex>
      </Flex>
    ),
    [theme]
  );
};
